import { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  approveFeinplanungRequest,
  fetchPendingFeinplanungRequests,
  rejectFeinplanungRequest,
  type PlanungFeinplanungRequest,
} from "@/lib/montageplanung";
import { formatDateDe } from "./dateUtils";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

function formatRequestedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function requesterLabel(request: PlanungFeinplanungRequest): string {
  const display = request.employee_display?.trim();
  if (display && display.toLowerCase() !== request.employee_login.toLowerCase()) {
    return `${display} (${request.employee_login})`;
  }
  return display || request.employee_login;
}

export interface FinePlanningRequestsDialogProps {
  open: boolean;
  onClose: () => void;
  onReviewed?: () => void;
  onHighlightInPlan?: (request: PlanungFeinplanungRequest) => void;
}

export function FinePlanningRequestsDialog({
  open,
  onClose,
  onReviewed,
  onHighlightInPlan,
}: FinePlanningRequestsDialogProps) {
  const [requests, setRequests] = useState<PlanungFeinplanungRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRequests(await fetchPendingFeinplanungRequests());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Anträge konnten nicht geladen werden.",
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  async function handleApprove(request: PlanungFeinplanungRequest) {
    setApprovingId(request.id);
    setError(null);
    try {
      await approveFeinplanungRequest(request.id);
      onReviewed?.();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Freigabe fehlgeschlagen.");
    } finally {
      setApprovingId(null);
    }
  }

  async function handleReject(request: PlanungFeinplanungRequest) {
    const ok = window.confirm(
      `Antrag von ${requesterLabel(request)} für „${request.assignment_title}" wirklich ablehnen?`,
    );
    if (!ok) return;
    setRejectingId(request.id);
    setError(null);
    try {
      await rejectFeinplanungRequest(request.id);
      onReviewed?.();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setRejectingId(null);
    }
  }

  const busy = approvingId != null || rejectingId != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Feinplanungs-Anträge"
      description="Monteure beantragen die Bearbeitung der Feinplanung für Einsätze im Montageplan."
      size="lg"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : requests.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine offenen Anträge.</p>
        ) : (
          <ul className="space-y-3">
            {requests.map((request) => {
              const who = requesterLabel(request);
              const isApproving = approvingId === request.id;
              const isRejecting = rejectingId === request.id;
              return (
                <li
                  key={request.id}
                  className="rounded-xl border border-separator-opaque bg-card p-4 shadow-sm"
                >
                  <div className="space-y-3">
                    <div className="rounded-lg border border-status-orange/30 bg-status-orange/10 px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-status-orange">
                        Freigabe für
                      </p>
                      <p className="mt-0.5 text-base font-semibold text-label">{who}</p>
                      {request.row_label ? (
                        <p className="mt-0.5 text-xs text-label-secondary">
                          Planzeile: {request.row_label}
                        </p>
                      ) : null}
                      <p className="mt-2 text-xs leading-relaxed text-label-secondary">
                        Nach der Freigabe kann{" "}
                        <span className="font-medium text-label">{who}</span> die Feinplanung
                        für den unten genannten Einsatz bearbeiten.
                      </p>
                    </div>

                    <div className="min-w-0 space-y-1">
                      <h3 className="text-sm font-semibold text-label">
                        {request.assignment_title}
                      </h3>
                      <p className="text-xs text-label-secondary">
                        {formatDateDe(request.assignment_date_from)} –{" "}
                        {formatDateDe(request.assignment_date_to)}
                      </p>
                      <p className="text-xs text-label-tertiary">
                        Beantragt am {formatRequestedAt(request.requested_at)}
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2 border-t border-separator pt-3">
                      {onHighlightInPlan ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="mr-auto"
                          disabled={busy}
                          onClick={() => onHighlightInPlan(request)}
                        >
                          Im Plan hervorheben
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={isRejecting}
                        disabled={busy}
                        onClick={() => void handleReject(request)}
                      >
                        Ablehnen
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        loading={isApproving}
                        disabled={busy}
                        onClick={() => void handleApprove(request)}
                      >
                        Freigeben
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
